import {forwardRef, ReactNode, MouseEvent} from "react";
import {Link as RouterLink} from "react-router-dom";
import {ListItemButton, ListItemIcon, ListItemText} from "@mui/material";
import {styled} from "@mui/material/styles";
import {t} from "../../../styles/theme/tokens";
import {MENU_ITEM_HEIGHT} from "./constants";

/**
 * ============================================================================
 * MenuItem — атом пункта бокового меню.
 * ============================================================================
 *
 * Поведение:
 *   - Если задан `to` → рендерится как <Link> от react-router (поддержка
 *     Cmd+Click для открытия в новой вкладке, корректный href в HTML).
 *   - Если задан `onClick` (без `to`) → рендерится как кнопка (для триггера
 *     раскрытия collapse-группы).
 *
 * Активное состояние:
 *   - Полоса 3px primary слева через ::before (border-radius на правом крае).
 *   - Фон action.selected.
 *   - Иконка и текст → text.primary.
 *   - Если задана `iconActive`, она подставляется вместо `icon`.
 *
 * Specificity:
 *   - `&&.Mui-selected` через двойной амперсанд для перебивания MUI defaults.
 *   - В MUI v6 ListItemButton.Mui-selected — селектор (0,3,0).
 *
 * Визуальное равновесие:
 *   - НЕ меняем font-weight на активном (это даёт layout shift).
 *   - Отличие через цвет + фон + indicator + filled-иконку.
 * ============================================================================
 */

interface MenuItemProps {
    icon: ReactNode;
    iconActive?: ReactNode;
    label: string;
    /** Если задан — пункт работает как ссылка. */
    to?: string;
    /** Если задан и нет `to` — пункт работает как кнопка. */
    onClick?: (e: MouseEvent) => void;
    isActive?: boolean;
    /** Контент справа (chevron, badge, и т.д.). */
    endAdornment?: ReactNode;
    /** Уровень вложенности (для пунктов внутри сворачиваемой группы). */
    indent?: number;
    /** aria-current="page" — для активной ссылки. По умолчанию вычисляется по isActive. */
    ariaCurrent?: boolean;
    /** Передаётся в ListItemButton (для collapse trigger используем aria-expanded). */
    ariaExpanded?: boolean;
}

const StyledItemButton = styled(ListItemButton, {
    shouldForwardProp: (prop) => prop !== "isActive" && prop !== "indent",
})<{ isActive?: boolean; indent?: number }>(({isActive, indent = 0}) => ({
    position: "relative",
    minHeight: MENU_ITEM_HEIGHT,
    height: MENU_ITEM_HEIGHT,
    borderRadius: t.radius.sm,
    marginInline: 8,
    paddingLeft: 12 + indent * 16,
    paddingRight: 8,
    gap: 0,
    color: t.text.secondary,
    transition: `background-color ${t.duration.fast}ms, color ${t.duration.fast}ms`,

    "&:hover": {
        backgroundColor: t.action.hover,
        color: t.text.primary,
    },

    // Indicator слева — 3px полоса primary при активном состоянии.
    // Позиционируем АБСОЛЮТНО в зоне marginInline:8, поэтому left:-8
    // выводит её к самому краю drawer'а (visual hierarchy: "пункт от края").
    "&::before": {
        content: '""',
        position: "absolute",
        left: -8,
        top: 6,
        bottom: 6,
        width: 3,
        backgroundColor: t.primary.main,
        borderRadius: "0 2px 2px 0",
        opacity: 0,
        transform: "scaleY(0.4)",
        transformOrigin: "center",
        transition: `opacity ${t.duration.base}ms, transform ${t.duration.base}ms`,
    },

    // && — перебиваем .MuiButtonBase-root.MuiListItemButton-root.Mui-selected (0,3,0)
    "&&.Mui-selected": {
        backgroundColor: t.action.selected,
        color: t.text.primary,

        "&:hover": {
            backgroundColor: t.action.selected,
        },

        "&::before": {
            opacity: 1,
            transform: "scaleY(1)",
        },
    },

    "&.Mui-focusVisible": {
        outline: `2px solid ${t.primary.alpha(0.6)}`,
        outlineOffset: -2,
    },

    // Запасной слой: если isActive передан напрямую (link без Mui-selected),
    // дублируем активный стиль. ListItemButton selected={true} обычно ставит
    // класс, но для безопасности оставляем оба механизма синхронизированными.
    ...(isActive && {
        backgroundColor: t.action.selected,
        color: t.text.primary,

        "&:hover": {
            backgroundColor: t.action.selected,
        },

        "&::before": {
            opacity: 1,
            transform: "scaleY(1)",
        },
    }),
}));

const StyledIcon = styled(ListItemIcon)({
    minWidth: 0,
    marginRight: 10,
    color: "inherit",                  // наследует от родителя (text.secondary / text.primary)

    "& > svg": {
        fontSize: 20,
        transition: `color ${t.duration.fast}ms`,
    },
});

const StyledText = styled(ListItemText)({
    margin: 0,

    "& .MuiListItemText-primary": {
        fontSize: "0.8125rem",         // 13px — плотный, читаемый, не "детский"
        fontWeight: 500,
        lineHeight: 1.2,
        color: "inherit",
        letterSpacing: "0.005em",
    },
});

const EndAdornmentSlot = styled("div")({
    marginLeft: 8,
    display: "flex",
    alignItems: "center",
    color: "inherit",
    flexShrink: 0,

    "& > svg": {
        fontSize: 18,
    },
});

const MenuItem = forwardRef<HTMLDivElement, MenuItemProps>(function MenuItem(
    {
        icon,
        iconActive,
        label,
        to,
        onClick,
        isActive = false,
        endAdornment,
        indent = 0,
        ariaCurrent,
        ariaExpanded,
    },
    ref
) {
    const displayedIcon = isActive && iconActive ? iconActive : icon;

    // ListItemButton может быть как кнопкой, так и Link через component prop.
    // Когда `to` задан — используем react-router Link для нативной поддержки
    // навигации (Cmd+Click работает, screen readers получают <a href>).
    const linkProps = to
        ? {component: RouterLink, to}
        : {component: "button" as const, type: "button" as const, onClick};

    return (
        <StyledItemButton
            ref={ref as never}
            {...linkProps}
            selected={isActive}
            isActive={isActive}
            indent={indent}
            aria-current={(ariaCurrent ?? isActive) ? "page" : undefined}
            aria-expanded={ariaExpanded}
            disableRipple
        >
            <StyledIcon>{displayedIcon}</StyledIcon>
            <StyledText primary={label}/>
            {endAdornment && <EndAdornmentSlot>{endAdornment}</EndAdornmentSlot>}
        </StyledItemButton>
    );
});

export default MenuItem;